import { useState } from 'react'
import { EmojiPicker } from 'frimousse'
import { Plus } from 'lucide-react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'

// frimousse has no built-in "frequently used" section, so we track picks in
// localStorage and render our own row, padded with sensible defaults
const FREQUENT_EMOJIS_KEY = 'signal_frequent_emojis'
const FREQUENT_EMOJIS_SHOWN = 6
export const DEFAULT_FREQUENT_EMOJIS = ['👍', '😂', '😢', '😐', '😊', '😍']

function readFrequentEmojis(): string[] {
  let byUsage: string[] = []
  try {
    const counts: Record<string, number> = JSON.parse(
      localStorage.getItem(FREQUENT_EMOJIS_KEY) || '{}'
    )
    byUsage = Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .map(([emoji]) => emoji)
  } catch {
    // Corrupt storage: fall through to the defaults
  }
  const padded = [...byUsage, ...DEFAULT_FREQUENT_EMOJIS.filter((e) => !byUsage.includes(e))]
  return padded.slice(0, FREQUENT_EMOJIS_SHOWN)
}

function trackEmojiUse(emoji: string) {
  try {
    const counts: Record<string, number> = JSON.parse(
      localStorage.getItem(FREQUENT_EMOJIS_KEY) || '{}'
    )
    counts[emoji] = (counts[emoji] || 0) + 1
    localStorage.setItem(FREQUENT_EMOJIS_KEY, JSON.stringify(counts))
  } catch {
    // Tracking is best-effort; never block the pick itself
  }
}

interface ReactionEmojiBarProps {
  emojis: string[]
  // OutboundMessage::MAX_REACTION_EMOJIS, served via form props
  maxEmojis: number
  onChange: (emojis: string[]) => void
}

function EmojiPickerPanel({
  onSelect,
  onRemove,
}: {
  onSelect: (emoji: string) => void
  onRemove?: () => void
}) {
  const [search, setSearch] = useState('')
  // Snapshot on open so the row doesn't reshuffle while picking
  const [frequentEmojis] = useState(readFrequentEmojis)

  const pick = (emoji: string) => {
    trackEmojiUse(emoji)
    onSelect(emoji)
  }

  return (
    <div className="flex flex-col">
      {onRemove && (
        <div className="flex justify-end px-3 pt-2">
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-gray-600 underline hover:text-gray-900"
          >
            Remover emoji
          </button>
        </div>
      )}
      <EmojiPicker.Root
        locale="pt"
        columns={8}
        onEmojiSelect={(emoji) => pick(emoji.emoji)}
        // Self-hosted emojibase-data (public/emojibase): the CSP connect_src
        // does not allow frimousse's default CDN (cdn.jsdelivr.net)
        emojibaseUrl="/emojibase"
        className="flex h-80 w-72 flex-col"
      >
        <div className="p-2">
          <EmojiPicker.Search
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar emoji..."
            className="w-full rounded-md bg-gray-100 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {!search && (
          <div className="px-3 pb-1.5">
            <div className="pb-1.5 text-xs font-medium text-gray-500">Mais usados</div>
            <div className="flex gap-1">
              {frequentEmojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => pick(emoji)}
                  className="flex size-8 items-center justify-center rounded-md text-lg hover:bg-gray-100"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
        <EmojiPicker.Viewport className="relative flex-1 outline-none">
          <EmojiPicker.Loading className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
            Carregando…
          </EmojiPicker.Loading>
          <EmojiPicker.Empty className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
            Nenhum emoji encontrado
          </EmojiPicker.Empty>
          <EmojiPicker.List
            className="select-none pb-1.5"
            components={{
              CategoryHeader: ({ category, ...props }) => (
                <div
                  className="bg-white px-3 pt-3 pb-1.5 text-xs font-medium text-gray-500"
                  {...props}
                >
                  {category.label}
                </div>
              ),
              Row: ({ children, ...props }) => (
                <div className="scroll-my-1.5 px-1.5" {...props}>
                  {children}
                </div>
              ),
              Emoji: ({ emoji, ...props }) => (
                <button
                  type="button"
                  className="flex size-8 items-center justify-center rounded-md text-lg data-[active]:bg-gray-100"
                  {...props}
                >
                  {emoji.emoji}
                </button>
              ),
            }}
          />
        </EmojiPicker.Viewport>
      </EmojiPicker.Root>
    </div>
  )
}

// Intercom-style editable reaction bar: click an emoji to replace or remove
// it, click "+" to add another (up to maxEmojis)
export default function ReactionEmojiBar({ emojis, maxEmojis, onChange }: ReactionEmojiBarProps) {
  const [openPicker, setOpenPicker] = useState<number | 'add' | null>(null)

  const replaceAt = (index: number, emoji: string) => {
    // Picking an emoji already in the bar would create a duplicate; ignore it
    if (!emojis.includes(emoji)) {
      const next = [...emojis]
      next[index] = emoji
      onChange(next)
    }
    setOpenPicker(null)
  }

  const removeAt = (index: number) => {
    onChange(emojis.filter((_, i) => i !== index))
    setOpenPicker(null)
  }

  const add = (emoji: string) => {
    if (!emojis.includes(emoji)) {
      onChange([...emojis, emoji])
    }
    setOpenPicker(null)
  }

  return (
    <div className="flex items-center justify-center gap-2">
      {emojis.map((emoji, index) => (
        <Popover
          key={`${emoji}-${index}`}
          open={openPicker === index}
          onOpenChange={(open) => setOpenPicker(open ? index : null)}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Trocar emoji"
              className="w-9 h-9 flex items-center justify-center rounded-full text-lg hover:bg-gray-100 transition-colors data-[state=open]:bg-gray-100 data-[state=open]:ring-2 data-[state=open]:ring-gray-300 data-[state=open]:ring-dashed"
            >
              {emoji}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <EmojiPickerPanel
              onSelect={(selected) => replaceAt(index, selected)}
              onRemove={emojis.length > 1 ? () => removeAt(index) : undefined}
            />
          </PopoverContent>
        </Popover>
      ))}

      {emojis.length < maxEmojis && (
        <Popover
          open={openPicker === 'add'}
          onOpenChange={(open) => setOpenPicker(open ? 'add' : null)}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Adicionar emoji"
              className="w-9 h-9 flex items-center justify-center rounded-full border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <EmojiPickerPanel onSelect={add} />
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
